import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Clock } from 'lucide-react'
import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'
import { articles } from '@/data/articles'

const lineV = {
  hidden: { y: '110%' },
  visible: (i) => ({
    y: '0%',
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 },
  }),
}

export default function InsightsPage() {
  return (
    <main data-testid="insights-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-40 pb-24 px-6 md:px-12 lg:px-24 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" animate="visible">
            <div className="overflow-hidden mb-6">
              <motion.p
                custom={0} variants={lineV}
                className="text-[10px] tracking-[0.38em] uppercase font-body font-semibold text-[#16365F]"
              >
                Strive Partners Insights
              </motion.p>
            </div>
            <div className="overflow-hidden">
              <motion.h1
                custom={1} variants={lineV}
                className="text-[clamp(3rem,7vw,7rem)] font-heading font-black tracking-tighter leading-none text-[#0A0A0A]"
                data-testid="insights-heading"
              >
                Intelligence for<br />
                <span className="font-light text-[#16365F]">GCC leaders</span>
              </motion.h1>
            </div>
            <div className="overflow-hidden mt-6">
              <motion.p
                custom={2} variants={lineV}
                className="text-base font-body text-[#0A0A0A]/50 max-w-lg leading-relaxed"
              >
                Research, benchmarks, and practitioner perspectives on technology leadership hiring and GCC talent strategy.
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Articles list */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#F6F7F9]">
        <div className="max-w-7xl mx-auto">
          <div className="border-t border-[#E5E7EB]">
            {articles.map((article, i) => (
              <motion.div
                key={article.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 }}
                data-testid={`article-card-${i + 1}`}
              >
                <Link
                  to={`/insights/${article.slug}`}
                  className="group flex flex-col md:flex-row md:items-start gap-6 md:gap-12 py-10 border-b border-[#E5E7EB] hover:bg-white transition-colors duration-200 px-4 -mx-4"
                >
                  <div className="flex-shrink-0 w-28">
                    <p className="text-[9px] tracking-[0.3em] uppercase font-body font-semibold text-[#16365F] mb-1">
                      {article.category}
                    </p>
                    <p className="text-[9px] text-[#0A0A0A]/30 font-body">{article.date}</p>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg md:text-xl font-heading font-black text-[#0A0A0A] mb-3 tracking-tight leading-tight group-hover:text-[#0B1F3A] transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-sm font-body text-[#0A0A0A]/50 leading-relaxed mb-4 max-w-2xl">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-4">
                      <span className="inline-flex items-center gap-1.5 text-[9px] text-[#0A0A0A]/35 font-body">
                        <Clock size={11} /> {article.readingTime}
                      </span>
                      <span className="text-[9px] text-[#0A0A0A]/30 font-body">{article.author}</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 self-start pt-1">
                    <ArrowRight
                      size={14}
                      className="text-[#0A0A0A]/20 group-hover:text-[#0B1F3A] transition-colors duration-200"
                    />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-white border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <h3 className="text-2xl font-heading font-black tracking-tight text-[#0A0A0A] mb-2">
                Looking to discuss a mandate?
              </h3>
              <p className="text-sm font-body text-[#0A0A0A]/50">We respond within one business day.</p>
            </div>
            <Link
              to="/contact"
              data-testid="insights-contact-cta"
              className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white text-[10px] font-body font-semibold tracking-[0.18em] uppercase px-7 py-4 rounded-none hover:bg-[#0B1F3A] transition-colors whitespace-nowrap"
            >
              Start Your Search <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  )
}
